import {ValueObject} from '@s-ui/domain'

import {QueryParamSharedValueObject} from '../../shared/ValueObjects/QueryParamSharedValueObject.js'
import {FilterIdsArraySearchValueObject} from './FilterIdsArraySearchValueObject.js'
import {FilterNumberSearchValueObject} from './FilterNumberSearchValueObject.js'

const FIRST_INDEX = 0
const SINGLE_OFFER_TYPE = 1
const TWO_OFFER_TYPES = 2

export class FilterOfferTypeIdsSearchValueObject extends ValueObject {
  #filteredOfferTypeIds
  #offerTypeIds

  constructor({filteredOfferTypeIds, offerTypeIds}) {
    super()
    this.#filteredOfferTypeIds = filteredOfferTypeIds
    this.#offerTypeIds = offerTypeIds
  }

  static create({filteredOfferTypeIds, offerTypeIds}) {
    return new FilterOfferTypeIdsSearchValueObject({
      filteredOfferTypeIds: FilterNumberSearchValueObject.create(filteredOfferTypeIds),
      offerTypeIds: FilterIdsArraySearchValueObject.create(offerTypeIds)
    })
  }

  static getDefaultOfferTypeIds({config, listingSectionId, offerTypeIds}) {
    const {DEFAULT_OFFER_TYPES_IDS_BY_LISTING_SECTION_ID, DEFAULT_OFFER_TYPE_IDS_SEARCH} = config.get('searchFilters')

    if (offerTypeIds) return offerTypeIds
    if (!listingSectionId) return DEFAULT_OFFER_TYPE_IDS_SEARCH
    return DEFAULT_OFFER_TYPES_IDS_BY_LISTING_SECTION_ID[listingSectionId] ?? DEFAULT_OFFER_TYPE_IDS_SEARCH
  }

  static getOfferTypeIds({config, filteredOfferTypeId, offerTypeId}) {
    const {ALL_OFFER_TYPES, OFFER_TYPE_KM0, OFFER_TYPE_SECOND_HAND, UNSELECTED_OFFER_TYPE} = config.get('searchFilters')

    const hasKm0OfferTypeEnabled = Boolean(OFFER_TYPE_KM0)

    if (!offerTypeId && !filteredOfferTypeId) {
      return []
    }

    if (offerTypeId === UNSELECTED_OFFER_TYPE) {
      const hasOfferTypeFilter =
        typeof filteredOfferTypeId === 'number' && filteredOfferTypeId !== UNSELECTED_OFFER_TYPE

      if (hasOfferTypeFilter) return [filteredOfferTypeId]

      return ALL_OFFER_TYPES
    }

    const hasOfferTypeFilter = typeof filteredOfferTypeId === 'number' && filteredOfferTypeId !== UNSELECTED_OFFER_TYPE

    // For km-0 with a selected filter -> [filteredOfferTypeId]
    if (hasOfferTypeFilter) return [filteredOfferTypeId]

    // For km-0 without selected filter -> [1,2,3,4,5]
    if (hasKm0OfferTypeEnabled && offerTypeId === OFFER_TYPE_KM0) {
      return [1, 2, 3, 4, 5]
    }

    // When offerTypeId is second hand (0)  -> [0,2,3,4,5]
    if (hasKm0OfferTypeEnabled && offerTypeId === OFFER_TYPE_SECOND_HAND) {
      return [0, 2, 3, 4, 5]
    }

    // Rest -> [offerTypeId]
    return typeof offerTypeId === 'number' ? [offerTypeId] : undefined
  }

  static getFilteredOfferTypeIds({config, listingSectionId, offerTypeIds}) {
    const isMotos = config.get('APP_NAME') === config.get('APP_NAME_MOTOS')
    const {LISTING_SECTION_IDS} = config.get('COMMON')
    const {OFFER_TYPE_KM0, OFFER_TYPE_SECOND_HAND, OFFER_TYPE_SUBSCRIPTION, UNSELECTED_OFFER_TYPE} =
      config.get('searchFilters')

    const hasMultiplesOfferTypes = offerTypeIds.length > SINGLE_OFFER_TYPE

    const [firstOfferType] = offerTypeIds

    if (listingSectionId === LISTING_SECTION_IDS.RENTING_VEHICLES) {
      return {offerTypeId: OFFER_TYPE_SUBSCRIPTION, filteredOfferTypeId: UNSELECTED_OFFER_TYPE}
    }

    const offerTypeFilters = {
      offerTypeId: firstOfferType,
      filteredOfferTypeId: undefined
    }

    if (listingSectionId === LISTING_SECTION_IDS.ALL_VEHICLES) {
      offerTypeFilters.offerTypeId = UNSELECTED_OFFER_TYPE
      offerTypeFilters.filteredOfferTypeId = hasMultiplesOfferTypes
        ? UNSELECTED_OFFER_TYPE
        : firstOfferType ?? UNSELECTED_OFFER_TYPE
      return offerTypeFilters
    }

    if (isMotos) return offerTypeFilters

    /**
     * If there are multiple offerTypeIds
     * [1,2,3,4,5] -> /km-0/
     * [2, X] -> /km-0/?OfferType=X
     */
    if (offerTypeIds.length > SINGLE_OFFER_TYPE && offerTypeIds.indexOf(OFFER_TYPE_KM0) >= FIRST_INDEX) {
      const filteredOfferTypeId =
        offerTypeIds.length === TWO_OFFER_TYPES
          ? offerTypeIds.find(offerType => offerType !== OFFER_TYPE_KM0) // [2, X]
          : UNSELECTED_OFFER_TYPE // [1,2,3,4,5]

      offerTypeFilters.offerTypeId = OFFER_TYPE_KM0
      offerTypeFilters.filteredOfferTypeId = filteredOfferTypeId
    }

    /**
     * If there is only one offerTypeId
     * [0] -> /segunda-mano/
     * [1] -> /nuevo/km-0
     * [2] -> /km-0/
     * [3] -> /km-0/
     * [4] -> /km-0/
     * [5] -> /km-0/
     */
    if (offerTypeIds.length === SINGLE_OFFER_TYPE && firstOfferType !== OFFER_TYPE_SECOND_HAND) {
      offerTypeFilters.offerTypeId = OFFER_TYPE_KM0
      offerTypeFilters.filteredOfferTypeId = firstOfferType
    }

    return offerTypeFilters
  }

  static createFromFilters({config, filteredOfferTypeId, listingSectionId, offerTypeId}) {
    const offerTypeIds = FilterOfferTypeIdsSearchValueObject.getOfferTypeIds({
      config,
      filteredOfferTypeId,
      listingSectionId,
      offerTypeId
    })

    return new FilterOfferTypeIdsSearchValueObject({
      filteredOfferTypeIds: FilterIdsArraySearchValueObject.createForNumbers({value: [filteredOfferTypeId]}),
      offerTypeIds: FilterIdsArraySearchValueObject.createForNumbers({
        value: offerTypeIds
      })
    })
  }

  static createFromApi({config, listingSectionId, offerTypeIds}) {
    const {DEFAULT_OFFER_TYPES_IDS_BY_LISTING_SECTION_ID, DEFAULT_OFFER_TYPE_IDS_SEARCH} = config.get('searchFilters')

    const {filteredOfferTypeId, offerTypeId} = FilterOfferTypeIdsSearchValueObject.getFilteredOfferTypeIds({
      config,
      offerTypeIds: FilterOfferTypeIdsSearchValueObject.getDefaultOfferTypeIds({
        config,
        listingSectionId,
        offerTypeIds:
          offerTypeIds ??
          DEFAULT_OFFER_TYPES_IDS_BY_LISTING_SECTION_ID[listingSectionId] ??
          DEFAULT_OFFER_TYPE_IDS_SEARCH
      }),
      listingSectionId
    })

    return new FilterOfferTypeIdsSearchValueObject({
      filteredOfferTypeIds: FilterIdsArraySearchValueObject.createForNumbers({
        value: [filteredOfferTypeId]
      }),
      offerTypeIds: FilterIdsArraySearchValueObject.createForNumbers({
        value: [offerTypeId]
      })
    })
  }

  static createQueryParams({config, filteredOfferTypeId, listingSectionId, offerTypeId}) {
    const {LISTING_SECTION_IDS} = config.get('COMMON')
    const {
      FILTER_NAMES,
      FILTER_QUERY_PARAMS,
      OFFER_TYPE_KM0,
      OFFER_TYPE_SECOND_HAND,
      OFFER_TYPE_SUBSCRIPTION,
      UNSELECTED_OFFER_TYPE
    } = config.get('searchFilters')

    if (listingSectionId === LISTING_SECTION_IDS.RENTING_VEHICLES) {
      return {offerTypeId: OFFER_TYPE_SUBSCRIPTION, filteredOfferTypeId: UNSELECTED_OFFER_TYPE}
    }

    const isListingSectionWidthAllOfferTypes =
      listingSectionId === LISTING_SECTION_IDS.ALL_VEHICLES ||
      listingSectionId === LISTING_SECTION_IDS.BUGGIES_VEHICLES ||
      listingSectionId === LISTING_SECTION_IDS.MINIMOTOS_VEHICLES ||
      listingSectionId === LISTING_SECTION_IDS.QUADS_AND_ATVS_VEHICLES

    const isKm0ListingSection = offerTypeId === OFFER_TYPE_KM0

    const defaultOfferType =
      isListingSectionWidthAllOfferTypes || isKm0ListingSection ? UNSELECTED_OFFER_TYPE : OFFER_TYPE_SECOND_HAND

    return new FilterOfferTypeIdsSearchValueObject({
      offerTypeIds: QueryParamSharedValueObject.createForSingle({
        key: FILTER_QUERY_PARAMS[FILTER_NAMES.OFFER_TYPE],
        value: filteredOfferTypeId === defaultOfferType ? null : filteredOfferTypeId
      })
    })
  }

  get isEmpty() {
    return this.#offerTypeIds.isEmpty
  }

  get queryParams() {
    return {
      ...(!this.isEmpty && {...this.#offerTypeIds.queryParam})
    }
  }

  get filteredOfferTypeId() {
    const [offerTypeId] = this.#filteredOfferTypeIds.value
    return offerTypeId
  }

  get offerTypeId() {
    const [offerTypeId] = this.#offerTypeIds.value
    return offerTypeId
  }

  get offerTypeIds() {
    return this.#offerTypeIds.value
  }

  get value() {
    return {filteredOfferTypeIds: this.#filteredOfferTypeIds.value, offerTypeIds: this.#offerTypeIds.value}
  }

  toJSON() {
    return {filteredOfferTypeIds: this.#filteredOfferTypeIds.toJSON(), offerTypeIds: this.#offerTypeIds.toJSON()}
  }
}
